import type { TipoRack } from '../api';
import { anchoNecesarioMm, code128, PUNTOS_MINIMOS, trazar } from './code128';

/**
 * Etiquetas de ubicación: una por posición y nivel de cada rack, más la de la
 * estación de fraccionamiento y la de cada zona de piso. El formato lo fija el
 * contrato con Operar (`docs/flujo-operativo-codigos-de-barras.md`, sección 3):
 * `U-<posición>-<nivel>`, `E-<posición>` y `Z-<posición>`, separados por el
 * **último** guion. La posición de un rack es
 * `${sector}${numero}-${columna de dos cifras}` — la misma cuenta que
 * `posicionCodigo` en `backend/src/domain/planta.ts` — y el rack ya se llama
 * `${sector}${numero}`, así que alcanza con pegarle el número de columna.
 */

export type RackParaEtiquetas = {
  /** `${sector}${numero}`, p. ej. «A1» o «F1». */
  id: string;
  columnas: number;
  niveles: number;
  tipo: TipoRack;
};

export type EtiquetaUbicacion = {
  /** El código que lee el escáner: `U-A1-04-2`, `E-F1-01` o `Z-P1-01`. */
  codigo: string;
  /**
   * La línea legible para una persona, debajo del código grande. Corta a
   * propósito —`A1-04 · Nivel 2`, no «Rack A1 · Posición 04 · Nivel 2»— para
   * poder agrandarla sin que se desborde en las etiquetas chicas, y así poder
   * leerla de lejos en las grandes.
   */
  texto: string;
  /**
   * Lo mismo que `texto`, partido para dibujarlo en casilleros: «RACK A1 ·
   * POSICIÓN 4 · NIVEL 2». Es lo que lee una persona si el escáner no anda,
   * así que va sin ceros adelante ni guiones. Null en la estación y en la zona.
   */
  partes: { rack: string; posicion: string; nivel: string } | null;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** La línea humana de una posición, o de la estación si no se le pasa columna/nivel. */
const textoHumano = (id: string, col?: number, nivel?: number) =>
  col === undefined ? `Estación ${id}` : `${id}-${pad2(col)} · Nivel ${nivel}`;

/**
 * Los códigos de un rack, en el orden en que conviene pegarlos recorriéndolo:
 * por columna y, dentro de cada una, del nivel más alto al más bajo — así se
 * pega de arriba hacia abajo sin tener que subir dos veces a la misma columna.
 * La estación es siempre una sola etiqueta, nivel 1 implícito. La zona de piso
 * también, sin nivel: es un área libre, una etiqueta para toda la zona.
 */
export function codigosDeUbicacion(rack: RackParaEtiquetas): EtiquetaUbicacion[] {
  if (rack.tipo === 'estacion') {
    return [{ codigo: `E-${rack.id}-01`, texto: textoHumano(rack.id), partes: null }];
  }
  if (rack.tipo === 'piso') return [etiquetaDeZona(rack.id)];

  const etiquetas: EtiquetaUbicacion[] = [];
  for (let col = 1; col <= rack.columnas; col++) {
    for (let nivel = rack.niveles; nivel >= 1; nivel--) {
      etiquetas.push(etiquetaDePosicion(rack.id, col, nivel));
    }
  }
  return etiquetas;
}

/** Una etiqueta `U-` con su texto y sus casilleros. */
export const etiquetaDePosicion = (rack: string, col: number, nivel: number): EtiquetaUbicacion => ({
  codigo: `U-${rack}-${pad2(col)}-${nivel}`,
  texto: textoHumano(rack, col, nivel),
  partes: { rack, posicion: String(col), nivel: String(nivel) },
});

/** La etiqueta `Z-` de una zona de piso: una sola para toda la zona. */
export const etiquetaDeZona = (rack: string): EtiquetaUbicacion => ({
  codigo: `Z-${rack}-01`,
  texto: `Zona de piso ${rack}`,
  partes: null,
});

/** Qué columnas y niveles de un rack se quieren imprimir. Ignorado en una estación o una zona. */
export type SeleccionRack = { desde: number; hasta: number; niveles: number[] };

/**
 * Las etiquetas elegidas a mano, para reimprimir sólo una parte de un rack (o
 * de varios) en vez de todo el depósito. Un rack entra si tiene una entrada en
 * `seleccion` —así una tarjeta sin tildar no imprime nada—; el rango se acota
 * al tamaño real del rack por si quedó una selección vieja de cuando tenía
 * menos columnas. La estación y la zona de piso ignoran el rango: son una sola
 * etiqueta siempre que estén marcadas, columnas y niveles no les dicen nada.
 */
export function etiquetasElegidas(
  racks: RackParaEtiquetas[],
  seleccion: Record<string, SeleccionRack>,
): EtiquetaUbicacion[] {
  const etiquetas: EtiquetaUbicacion[] = [];
  for (const rack of racks) {
    const sel = seleccion[rack.id];
    if (!sel) continue;

    if (rack.tipo !== 'rack') {
      etiquetas.push(...codigosDeUbicacion(rack));
      continue;
    }

    const desde = Math.max(1, sel.desde);
    const hasta = Math.min(rack.columnas, sel.hasta);
    for (let col = desde; col <= hasta; col++) {
      for (let nivel = rack.niveles; nivel >= 1; nivel--) {
        if (!sel.niveles.includes(nivel)) continue;
        etiquetas.push(etiquetaDePosicion(rack.id, col, nivel));
      }
    }
  }
  return etiquetas;
}

const escapar = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Medida de una etiqueta, en milímetros. Una de `TAMANOS` (`/etiquetas/catalogo`). */
export type MedidaEtiqueta = { ancho: number; alto: number };

/**
 * Cómo se lee la etiqueta pegada. El rollo no cambia: el papel sale siempre
 * con su ancho, y lo que se gira es el dibujo —igual que la orientación
 * «apaisada» del diseñador de etiquetas (`Etiqueta.tsx`)—. Sin elegir, la
 * etiqueta se lee como sale del rollo.
 */
export type Orientacion = 'horizontal' | 'vertical';

const MEDIDA_PLANTA: MedidaEtiqueta = { ancho: 70, alto: 30 };

/** La orientación en que sale del rollo sin girar. Cuadrada cuenta como horizontal. */
export const orientacionNatural = (m: MedidaEtiqueta): Orientacion => (m.alto > m.ancho ? 'vertical' : 'horizontal');

/** Sobre qué superficie se dibuja: el papel, o el papel acostado si hay que girarlo. */
export function areaDeDibujo(m: MedidaEtiqueta, orientacion: Orientacion = orientacionNatural(m)) {
  const girar = orientacion !== orientacionNatural(m) && m.ancho !== m.alto;
  return { ancho: girar ? m.alto : m.ancho, alto: girar ? m.ancho : m.alto, girar };
}

/** Margen a los costados del código de barras, para un área de ese ancho. */
export const margenDeCodigo = (anchoArea: number) => Math.max(3, anchoArea * 0.07);

/**
 * Si el código de barras entra en esa medida y orientación sin bajar del
 * módulo mínimo. En vertical, una etiqueta chica (70×30) se queda con 30 mm
 * de ancho y el código no entra: la pantalla lo avisa antes de imprimir.
 */
export function codigoEntra(e: EtiquetaUbicacion, medida: MedidaEtiqueta, orientacion?: Orientacion): boolean {
  const { ancho } = areaDeDibujo(medida, orientacion);
  const codigo = code128(e.codigo);
  return codigo !== null && anchoNecesarioMm(codigo, PUNTOS_MINIMOS) <= ancho - margenDeCodigo(ancho) * 2;
}

/**
 * El dibujo de una etiqueta de ubicación: arriba el código de barras, bien
 * alto, y abajo la ubicación en casilleros grandes —RACK · POSICIÓN · NIVEL—,
 * que es lo que se lee de lejos o si el escáner falla. Se arma a mano con
 * `code128()`/`trazar()` en vez del editor de plantillas: es una sola etiqueta
 * fija, y una plantilla sería una vuelta para algo que no se va a tocar nunca.
 *
 * Todas las proporciones salen del área de dibujo, así que una sola función
 * dibuja las cinco de `TAMANOS` en las dos orientaciones.
 */
export function svgEtiquetaUbicacion(
  e: EtiquetaUbicacion,
  medida: MedidaEtiqueta = MEDIDA_PLANTA,
  orientacion?: Orientacion,
): string {
  const { ancho: W, alto: H, girar } = areaDeDibujo(medida, orientacion);

  const margenX = margenDeCodigo(W);
  const barY = Math.max(2, H * 0.08);
  const barAncho = W - margenX * 2;
  // Sin el código en texto debajo, las barras se quedan con ese lugar: más
  // altas se leen desde más lejos y más torcidas. Con tope, porque una de
  // 150 mm de alto dibujaría 70 mm de barras, a costa de los casilleros.
  const barAlto = Math.min(H * 0.47, 50);

  // No alcanza con que `code128()` lo pueda codificar: con el ancho que hay,
  // el módulo más fino tiene que entrar en la zona muda de la norma (ver
  // `code128.ts`). Si no entra ni al mínimo, mejor el aviso que un código que
  // el escáner lee mal una vez sí y la otra no.
  const codigo = code128(e.codigo);
  const barras = codigoEntra(e, medida, orientacion)
    ? trazar(codigo!, barAncho)
        .barras.map(
          (b) =>
            `<rect x="${(margenX + b.x).toFixed(3)}" y="${barY.toFixed(3)}" width="${b.ancho.toFixed(3)}" height="${barAlto.toFixed(3)}" fill="#000"/>`,
        )
        .join('')
    : `<text x="${W / 2}" y="${(barY + barAlto / 2).toFixed(3)}" font-size="${Math.max(2.5, W * 0.045).toFixed(2)}" ` +
      `text-anchor="middle" fill="#000">${codigo === null ? 'código no válido' : 'código no entra en esta medida'}</text>`;

  // Lo que queda debajo es para los casilleros. Cada uno lleva su rótulo
  // chico arriba y el valor grande abajo; el valor se agranda hasta donde
  // entra a lo ancho (según cuántos caracteres tiene) y a lo alto.
  const casilleros: [string, string][] = e.partes
    ? [
        ['RACK', e.partes.rack],
        ['POSICIÓN', e.partes.posicion],
        ['NIVEL', e.partes.nivel],
      ]
    : e.codigo.startsWith('Z-')
      ? [['ZONA DE PISO', e.texto.replace(/^Zona de piso /, '')]]
      : [['ESTACIÓN', e.texto.replace(/^Estación /, '')]];
  const arriba = barY + barAlto + Math.max(1.5, H * 0.04);
  const abajo = H - Math.max(1.5, H * 0.04);
  const largoValor = Math.max(...casilleros.map(([, v]) => v.length));
  const largoRotulo = Math.max(...casilleros.map(([r]) => r.length));
  const altoLibre = Math.max(0, abajo - arriba);
  /** Qué tan grande sale el valor si los casilleros se reparten así. */
  const valorCon = (filas: number, columnas: number) =>
    Math.min((barAncho / columnas) * 0.86 / (largoValor * 0.62), (altoLibre / filas) * 0.62);
  // Uno al lado del otro, o apilados si así el valor sale casi igual de grande
  // (hasta un 25 % menos): en una etiqueta alta, en fila quedan con medio
  // papel en blanco abajo, y apilados lo llenan.
  const n = casilleros.length;
  const apilados = n > 1 && valorCon(n, 1) >= valorCon(1, n) * 0.75;
  const filas = apilados ? n : 1;
  const columnas = apilados ? 1 : n;
  const altoCasillero = altoLibre / filas;
  const anchoCasillero = barAncho / columnas;
  const valorFont = Math.max(3, valorCon(filas, columnas));
  const rotuloFont = Math.max(1.6, Math.min(valorFont * 0.3, (anchoCasillero * 0.9) / (largoRotulo * 0.66)));
  // El bloque (rótulo + valor) se centra en el alto de su casillero.
  const bloque = rotuloFont * 1.5 + valorFont * 0.74;

  const celdas = casilleros
    .map(([rotulo, valor], i) => {
      const col = apilados ? 0 : i;
      const fila = apilados ? i : 0;
      const x = margenX + anchoCasillero * (col + 0.5);
      const top = arriba + altoCasillero * fila;
      const rotuloY = top + (altoCasillero - bloque) / 2 + rotuloFont;
      const valorY = rotuloY + rotuloFont * 0.5 + valorFont * 0.74;
      // Línea y no <rect>: los <rect> negros son las barras, y la prueba los cuenta.
      const linea = (x1: number, y1: number, x2: number, y2: number) =>
        `<line x1="${x1.toFixed(3)}" y1="${y1.toFixed(3)}" x2="${x2.toFixed(3)}" y2="${y2.toFixed(3)}" stroke="#000" stroke-width="0.3"/>`;
      const separador =
        i === 0
          ? ''
          : apilados
            ? linea(margenX + barAncho * 0.1, top, margenX + barAncho * 0.9, top)
            : linea(margenX + anchoCasillero * col, top + altoCasillero * 0.1, margenX + anchoCasillero * col, top + altoCasillero * 0.9);
      return (
        separador +
        `<text x="${x.toFixed(3)}" y="${rotuloY.toFixed(3)}" font-family="Geist, Helvetica, Arial, sans-serif" ` +
        `font-size="${rotuloFont.toFixed(2)}" font-weight="600" text-anchor="middle" fill="#000">${rotulo}</text>` +
        `<text x="${x.toFixed(3)}" y="${valorY.toFixed(3)}" font-family="Geist, Helvetica, Arial, sans-serif" ` +
        `font-size="${valorFont.toFixed(2)}" font-weight="800" text-anchor="middle" fill="#000">${escapar(valor)}</text>`
      );
    })
    .join('');

  const dibujo =
    `<rect x="0" y="0" width="${W}" height="${H}" fill="#fff"/>` +
    barras +
    celdas;

  // Girada, se dibuja sobre el papel acostado y se apoya un cuarto de vuelta:
  // el mismo giro que usa el diseñador para las apaisadas.
  return (
    `<svg viewBox="0 0 ${medida.ancho} ${medida.alto}" xmlns="http://www.w3.org/2000/svg">` +
    (girar ? `<g transform="translate(${medida.ancho} 0) rotate(90)">${dibujo}</g>` : dibujo) +
    `</svg>`
  );
}
